import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Loader2, ImageOff, Globe, Leaf, ArrowUpRight, Cpu, ShieldCheck } from "lucide-react";
import api, { formatApiError } from "../lib/api";

const SPEC_LABELS = {
  processore: "Processore",
  ram: "RAM",
  hdd: "Storage",
  monitor: "Monitor",
  scheda_video: "Scheda Video",
  sistema_operativo: "Sistema Operativo",
};

const HERO_BG =
  "https://images.unsplash.com/photo-1517816743773-6e0fd518b4a6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwxfHxtaW5pbWFsaXN0JTIwd2hpdGUlMjBtb2Rlcm4lMjBhcmNoaXRlY3R1cmUlMjBhYnN0cmFjdHxlbnwwfHx8fDE3ODE3ODA5OTV8MA&ixlib=rb-4.1.0&q=85";

function Gallery({ images, accent, productIndex }) {
  const [active, setActive] = useState(0);
  useEffect(() => setActive(0), [images]);

  if (!images || images.length === 0) {
    return (
      <div className="aspect-square w-full rounded-3xl bg-white ring-1 ring-slate-900/5 flex items-center justify-center text-slate-200 shadow-sm">
        <ImageOff className="w-12 h-12" />
      </div>
    );
  }
  return (
    <div>
      <div className="relative aspect-square w-full rounded-3xl overflow-hidden bg-white ring-1 ring-slate-900/5 shadow-[0_20px_60px_-25px_rgba(15,23,42,0.25)]">
        <AnimatePresence mode="wait">
          <motion.img
            key={active}
            src={images[active]}
            alt=""
            data-testid="showcase-active-image"
            initial={{ opacity: 0, scale: 1.02 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.45, ease: "easeOut" }}
            className="absolute inset-0 w-full h-full object-cover"
          />
        </AnimatePresence>
        <div className="absolute top-4 left-4 text-[11px] font-semibold tracking-widest uppercase px-3 py-1 rounded-full bg-white/85 backdrop-blur text-slate-700 ring-1 ring-slate-900/5">
          {String(active + 1).padStart(2, "0")} / {String(images.length).padStart(2, "0")}
        </div>
      </div>
      {images.length > 1 && (
        <div className="flex gap-3 mt-4 overflow-x-auto pb-2 scrollbar-hide">
          {images.map((src, i) => (
            <button
              key={i}
              onClick={() => setActive(i)}
              data-testid={`showcase-thumbnail-${productIndex}-${i}`}
              className="shrink-0 w-20 h-20 rounded-2xl overflow-hidden bg-white ring-1 ring-slate-900/5 transition-all duration-300"
              style={{
                outline: i === active ? `2px solid ${accent}` : "2px solid transparent",
                outlineOffset: "2px",
                opacity: i === active ? 1 : 0.55,
              }}
            >
              <img src={src} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function ProductSection({ product, index, accent }) {
  const specs = Object.entries(product.specs || {}).filter(([, v]) => v);
  const paragraphs = (product.description || "").split("\n").map((p) => p.trim()).filter(Boolean);
  const isRefurb = product.condition === "ricondizionato";

  return (
    <motion.section
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className="py-16 sm:py-24 border-b border-slate-200 last:border-0"
      data-testid={`showcase-product-${index}`}
    >
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
        {/* Gallery — sticky on desktop */}
        <div className="lg:col-span-6 lg:sticky lg:top-24">
          <Gallery images={product.gallery} accent={accent} productIndex={index} />
        </div>

        {/* Content */}
        <div className="lg:col-span-6">
          <div className="flex items-center gap-3 mb-3">
            <span className="text-xs font-semibold uppercase tracking-[0.25em]" style={{ color: accent }}>
              Prodotto {String(index + 1).padStart(2, "0")}
            </span>
            {isRefurb && (
              <span
                data-testid={`showcase-refurb-badge-${index}`}
                className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide px-2.5 py-1 rounded-full bg-green-50 text-green-700 ring-1 ring-green-600/20"
              >
                <Leaf className="w-3 h-3" /> Ricondizionato · Green
              </span>
            )}
          </div>

          <h2
            data-testid={`product-title-${index}`}
            className="font-display text-4xl sm:text-5xl font-semibold tracking-tight text-slate-900 leading-[1.05] mb-6"
          >
            {product.title}
          </h2>

          <div data-testid={`product-description-${index}`} className="space-y-4">
            {paragraphs.map((p, i) => (
              <p key={i} className={i === 0 ? "text-xl text-slate-800 leading-relaxed font-medium" : "text-base text-slate-600 leading-relaxed"}>
                {p}
              </p>
            ))}
          </div>

          {specs.length > 0 && (
            <div className="mt-10">
              <div className="flex items-center gap-2 mb-4">
                <Cpu className="w-4 h-4" style={{ color: accent }} />
                <span className="text-xs font-semibold uppercase tracking-widest text-slate-500">Specifiche tecniche</span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4" data-testid={`product-specs-grid-${index}`}>
                {specs.map(([k, v]) => (
                  <div
                    key={k}
                    className="rounded-2xl bg-slate-50 p-4 ring-1 ring-slate-900/5 transition-all duration-300 hover:-translate-y-1 hover:shadow-md hover:bg-white"
                  >
                    <span className="block text-[11px] font-semibold uppercase tracking-widest text-slate-400 mb-1.5">
                      {SPEC_LABELS[k] || k}
                    </span>
                    <span className="block text-lg font-medium text-slate-900 leading-snug">{v}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {isRefurb && (
            <a
              href="https://www.86bit.it/green-fornitura/"
              target="_blank"
              rel="noreferrer"
              data-testid={`green-cta-button-${index}`}
              className="mt-10 group inline-flex items-center gap-2.5 px-6 py-3.5 rounded-full bg-green-600 hover:bg-green-700 text-white text-base font-medium no-underline shadow-lg shadow-green-600/20 transition-all duration-300 active:scale-95"
            >
              <Leaf className="w-5 h-5" />
              Scopri la Green Fornitura 86BIT
              <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
          )}
        </div>
      </div>
    </motion.section>
  );
}

export default function Showcase() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/public/quotes/${token}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(formatApiError(e.response?.data?.detail) || "Vetrina non disponibile"));
  }, [token]);

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center text-center px-6 bg-[#F8FAFC]">
        <h1 className="font-display text-3xl font-semibold text-slate-900">Vetrina non disponibile</h1>
        <p className="text-slate-500 mt-3 max-w-md" data-testid="showcase-error">{error}</p>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  const accent = data.company.brand_color || "#5B9BD5";
  const hasGreen = (data.products || []).some((p) => p.condition === "ricondizionato");

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-slate-900">
      {/* Sticky top bar */}
      <div className="sticky top-0 z-40 backdrop-blur-md bg-white/70 border-b border-slate-200/70">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          {data.company.logo
            ? <img src={data.company.logo} alt={data.company.company_name} className="h-9 w-auto object-contain" />
            : <span className="font-display font-bold text-lg text-slate-900">{data.company.company_name}</span>}
          {data.company.website && (
            <a
              href={data.company.website.startsWith("http") ? data.company.website : `https://${data.company.website}`}
              target="_blank"
              rel="noreferrer"
              className="text-sm font-medium inline-flex items-center gap-1.5 hover:underline"
              style={{ color: accent }}
            >
              <Globe className="w-4 h-4" /> {data.company.website.replace(/^https?:\/\//, "")}
            </a>
          )}
        </div>
      </div>

      {/* Hero */}
      <header className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-white/85 via-white/90 to-[#F8FAFC]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-20 sm:pt-32 sm:pb-28">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
            className="max-w-3xl"
          >
            <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.3em]" style={{ color: accent }}>
              <span className="w-8 h-px" style={{ backgroundColor: accent }} /> Catalogo prodotti
            </span>
            <h1
              data-testid="catalog-hero-title"
              className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tighter text-slate-900 mt-5 leading-[0.98]"
            >
              {data.title}
            </h1>
            {data.subtitle && (
              <p className="text-xl text-slate-600 mt-6 max-w-2xl leading-relaxed">{data.subtitle}</p>
            )}
            <div className="flex flex-wrap items-center gap-x-6 gap-y-3 mt-8 text-sm text-slate-500">
              <span className="inline-flex items-center gap-2">
                <span className="font-display text-2xl font-bold text-slate-900">{(data.products || []).length}</span>
                prodotti selezionati
              </span>
              {hasGreen && (
                <span className="inline-flex items-center gap-1.5 text-green-700 font-medium">
                  <Leaf className="w-4 h-4" /> Soluzioni Green disponibili
                </span>
              )}
              <span className="inline-flex items-center gap-1.5">
                <ShieldCheck className="w-4 h-4" style={{ color: accent }} /> Selezione business corporate
              </span>
            </div>
          </motion.div>
        </div>
      </header>

      {/* Products */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {(data.products || []).map((p, i) => (
          <ProductSection key={i} product={p} index={i} accent={accent} />
        ))}
      </main>

      {/* Footer */}
      <footer className="mt-8 border-t border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            {data.company.logo
              ? <img src={data.company.logo} alt={data.company.company_name} className="h-8 w-auto object-contain" />
              : <span className="font-display font-bold text-slate-900">{data.company.company_name}</span>}
          </div>
          <p className="text-sm text-slate-400">© {new Date().getFullYear()} {data.company.company_name}</p>
        </div>
      </footer>
    </div>
  );
}
